/**
 * FSRS-6.0 algorithm implementation for the Llemma app.
 *
 * Ported from ts-fsrs (https://github.com/open-spaced-repetition/ts-fsrs).
 * Keeps the full FSRS math (S, D, R, all 21 parameters) but drops
 * interval/due-date scheduling, fuzzing, learning-step timers, undo, and
 * reschedule — none of which apply to our event-driven, threshold-filtered
 * reading workflow.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export enum State {
  New = 0,
  Learning = 1,
  Review = 2,
  Relearning = 3,
}

export enum Rating {
  Again = 1,
  Hard = 2,
  Good = 3,
  Easy = 4,
}

export type Grade = Rating.Again | Rating.Hard | Rating.Good | Rating.Easy

/** Per-word memory state stored in the database. */
export interface WordMemory {
  stability: number
  difficulty: number
  state: State
  lastReview: number // epoch-ms
  lapses: number
  reps: number
}

/** Result of reviewing a word. */
export interface ReviewResult {
  memory: WordMemory
  retrievability: number // R at the moment of review
}

// ---------------------------------------------------------------------------
// Default FSRS-6.0 parameters (trained on millions of Anki reviews)
// ---------------------------------------------------------------------------

// prettier-ignore
export const DEFAULT_W: readonly number[] = Object.freeze([
  0.212,   // w0  initial stability (Again)
  1.2931,  // w1  initial stability (Hard)
  2.3065,  // w2  initial stability (Good)
  8.2956,  // w3  initial stability (Easy)
  6.4133,  // w4  initial difficulty base
  0.8334,  // w5  initial difficulty multiplier
  3.0194,  // w6  difficulty adjustment
  0.001,   // w7  mean reversion weight
  1.8722,  // w8  recall stability exponent
  0.1666,  // w9  stability negative power
  0.796,   // w10 retrievability exponent
  1.4835,  // w11 forgetting stability multiplier
  0.0614,  // w12 forgetting difficulty power
  0.2629,  // w13 forgetting stability power
  1.6483,  // w14 forgetting retrievability exponent
  0.6014,  // w15 hard penalty
  1.8729,  // w16 easy bonus
  0.5425,  // w17 short-term exponent
  0.0912,  // w18 short-term offset
  0.0658,  // w19 short-term last-stability exponent
  0.1542,  // w20 decay
])

const S_MIN = 0.001
const S_MAX = 36500.0
const MS_PER_DAY = 86_400_000

// ---------------------------------------------------------------------------
// Core math
// ---------------------------------------------------------------------------

/** Type-safe parameter access — indices are always in bounds for a 21-element array. */
const p = (params: readonly number[], i: number): number => params[i] as number

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function computeDecayFactor(params: readonly number[]) {
  const decay = -p(params, 20)
  const factor = Math.exp(Math.log(0.9) / decay) - 1
  return { decay, factor }
}

/**
 * Power-law forgetting curve.
 * R(t, S) = (1 + factor × t / S) ^ decay
 */
export function forgettingCurve(
  elapsedDays: number,
  stability: number,
  params: readonly number[] = DEFAULT_W,
): number {
  const { decay, factor } = computeDecayFactor(params)
  return Math.pow(1 + (factor * elapsedDays) / stability, decay)
}

/**
 * Calculate current retrievability for a word.
 * Returns a value in [0, 1] representing recall probability right now.
 */
export function getRetrievability(
  memory: WordMemory,
  now: number = Date.now(),
  params: readonly number[] = DEFAULT_W,
): number {
  if (memory.state === State.New) return 0
  const elapsedDays = Math.max(0, (now - memory.lastReview) / MS_PER_DAY)
  return forgettingCurve(elapsedDays, memory.stability, params)
}

// ---------------------------------------------------------------------------
// Initial values for new cards
// ---------------------------------------------------------------------------

/** S₀(G) = max(w[G-1], 0.1) */
function initStability(g: Grade, params: readonly number[]): number {
  return Math.max(p(params, g - 1), 0.1)
}

/** D₀(G) = w[4] - e^((G-1) × w[5]) + 1, clamped to [1, 10] */
function initDifficulty(g: Grade, params: readonly number[]): number {
  return clamp(p(params, 4) - Math.exp((g - 1) * p(params, 5)) + 1, 1, 10)
}

// ---------------------------------------------------------------------------
// Update formulas
// ---------------------------------------------------------------------------

function linearDamping(deltaD: number, oldD: number): number {
  return (deltaD * (10 - oldD)) / 9
}

function meanReversion(init: number, current: number, w7: number): number {
  return w7 * init + (1 - w7) * current
}

/** Difficulty after review. */
function nextDifficulty(d: number, g: Grade, params: readonly number[]): number {
  const deltaD = -p(params, 6) * (g - 3)
  const nextD = d + linearDamping(deltaD, d)
  return clamp(
    meanReversion(initDifficulty(Rating.Easy, params), nextD, p(params, 7)),
    1, 10,
  )
}

/** Stability after successful recall (Hard/Good/Easy). */
function nextRecallStability(
  d: number, s: number, r: number, g: Grade, params: readonly number[],
): number {
  const hardPenalty = g === Rating.Hard ? p(params, 15) : 1
  const easyBonus = g === Rating.Easy ? p(params, 16) : 1
  return clamp(
    s * (1 +
      Math.exp(p(params, 8)) *
      (11 - d) *
      Math.pow(s, -p(params, 9)) *
      (Math.exp((1 - r) * p(params, 10)) - 1) *
      hardPenalty *
      easyBonus),
    S_MIN,
    S_MAX,
  )
}

/** Stability after forgetting (Again). */
function nextForgetStability(
  d: number, s: number, r: number, params: readonly number[],
): number {
  return clamp(
    p(params, 11) *
    Math.pow(d, -p(params, 12)) *
    (Math.pow(s + 1, p(params, 13)) - 1) *
    Math.exp((1 - r) * p(params, 14)),
    S_MIN,
    S_MAX,
  )
}

/** Stability for short-term (same-day / intra-session) reviews. */
function nextShortTermStability(
  s: number, g: Grade, params: readonly number[],
): number {
  const sinc =
    Math.pow(s, -p(params, 19)) *
    Math.exp(p(params, 17) * (g - 3 + p(params, 18)))
  const maskedSinc = g >= Rating.Hard ? Math.max(sinc, 1) : sinc
  return clamp(s * maskedSinc, S_MIN, S_MAX)
}

/**
 * Compute the next memory state (S, D) given current state, elapsed time,
 * and grade. This is the `next_state` from ts-fsrs's FSRSAlgorithm.
 */
function nextMemoryState(
  memory: { stability: number; difficulty: number } | null,
  elapsedDays: number,
  g: Grade,
  params: readonly number[],
): { stability: number; difficulty: number } {
  const s = memory?.stability ?? 0
  const d = memory?.difficulty ?? 0

  // Brand new card — initialize
  if (d === 0 && s === 0) {
    return {
      stability: initStability(g, params),
      difficulty: clamp(initDifficulty(g, params), 1, 10),
    }
  }

  const r = forgettingCurve(elapsedDays, s, params)

  // Determine new stability
  const sAfterSuccess = nextRecallStability(d, s, r, g, params)
  const sAfterFail = nextForgetStability(d, s, r, params)
  const sAfterShortTerm = nextShortTermStability(s, g, params)

  let newS: number
  if (g === Rating.Again) {
    // Ceiling: post-lapse S must not exceed S / e^(w17 * w18)
    const ceiling = s / Math.exp(p(params, 17) * p(params, 18))
    newS = clamp(ceiling, S_MIN, sAfterFail)
  } else {
    newS = sAfterSuccess
  }

  // Short-term override for same-day reviews
  if (elapsedDays === 0) {
    newS = sAfterShortTerm
  }

  return {
    stability: newS,
    difficulty: nextDifficulty(d, g, params),
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Create a fresh memory entry for a word never seen before. */
export function createNewMemory(): WordMemory {
  return {
    stability: 0,
    difficulty: 0,
    state: State.New,
    lastReview: 0,
    lapses: 0,
    reps: 0,
  }
}

/**
 * Review a word and return the updated memory state.
 *
 * This is the only function the app needs to call after a flashcard is graded.
 * It handles all state transitions (New→Learning, Learning→Review, etc.)
 * and updates S and D using the full FSRS-6.0 math.
 */
export function review(
  memory: WordMemory,
  grade: Grade,
  now: number = Date.now(),
  params: readonly number[] = DEFAULT_W,
): ReviewResult {
  const elapsedDays = memory.lastReview > 0
    ? Math.max(0, (now - memory.lastReview) / MS_PER_DAY)
    : 0

  const r = memory.stability > 0
    ? forgettingCurve(elapsedDays, memory.stability, params)
    : 0

  const { stability, difficulty } = nextMemoryState(
    memory.state === State.New ? null : memory,
    elapsedDays,
    grade,
    params,
  )

  // State transitions
  let nextCardState: State
  let lapses = memory.lapses
  if (memory.state === State.New) {
    nextCardState = grade >= Rating.Good ? State.Review : State.Learning
  } else if (memory.state === State.Learning || memory.state === State.Relearning) {
    nextCardState = grade >= Rating.Good ? State.Review : memory.state
  } else {
    // Review state
    if (grade === Rating.Again) {
      nextCardState = State.Relearning
      lapses += 1
    } else {
      nextCardState = State.Review
    }
  }

  return {
    memory: {
      stability,
      difficulty,
      state: nextCardState,
      lastReview: now,
      lapses,
      reps: memory.reps + 1,
    },
    retrievability: r,
  }
}

/**
 * Determine whether a word should be shown in a pre-reading session.
 *
 * - New words: always shown
 * - Learning/Relearning words: always shown
 * - Review words: shown only if R ≤ threshold
 */
export function shouldShow(
  memory: WordMemory,
  threshold: number = 0.95,
  now: number = Date.now(),
  params: readonly number[] = DEFAULT_W,
): boolean {
  if (memory.state !== State.Review) return true
  return getRetrievability(memory, now, params) <= threshold
}
